import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiPrinter, FiDownload, FiArrowLeft, FiCheck } from 'react-icons/fi';
import paymentService from '../services/payment.service';

const Invoice = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchInvoiceDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  const fetchInvoiceDetails = async () => {
    try {
      setLoading(true);
      const response = await paymentService.getInvoiceDetails(invoiceId);
      if (response.success) {
        setInvoice(response.data);
      } else {
        toast.error('Failed to load invoice');
        navigate('/members');
      }
    } catch (error) {
      console.error('Fetch invoice error:', error);
      toast.error('Failed to load invoice');
      navigate('/members');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    // Get the invoice content
    const printContent = document.getElementById('invoice-content');

    if (!printContent) {
      toast.error('Unable to print invoice');
      return;
    }

    // Create a new window for printing
    const printWindow = window.open('', '', 'width=800,height=600');

    // Write the invoice content to the new window
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice - ${invoice.invoice_number}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
              padding: 20px;
              color: #000;
            }
            .invoice-container {
              max-width: 800px;
              margin: 0 auto;
            }
            table {
              width: 100%;
              border-collapse: collapse;
            }
            th, td {
              padding: 12px;
              text-align: left;
            }
            th {
              border-bottom: 2px solid #333;
              font-weight: 600;
            }
            td {
              border-bottom: 1px solid #ddd;
            }
            .text-right {
              text-align: right;
            }
            .text-center {
              text-align: center;
            }
            .font-bold {
              font-weight: 700;
            }
            .text-lg {
              font-size: 1.125rem;
            }
            .text-xl {
              font-size: 1.25rem;
            }
            .text-2xl {
              font-size: 1.5rem;
            }
            .text-3xl {
              font-size: 1.875rem;
            }
            .mb-1 { margin-bottom: 0.25rem; }
            .mb-2 { margin-bottom: 0.5rem; }
            .mb-3 { margin-bottom: 0.75rem; }
            .mb-4 { margin-bottom: 1rem; }
            .mb-6 { margin-bottom: 1.5rem; }
            .mb-8 { margin-bottom: 2rem; }
            .mt-1 { margin-top: 0.25rem; }
            .mt-2 { margin-top: 0.5rem; }
            .mt-3 { margin-top: 0.75rem; }
            .mt-4 { margin-top: 1rem; }
            .py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
            .py-3 { padding-top: 0.75rem; padding-bottom: 0.75rem; }
            .py-4 { padding-top: 1rem; padding-bottom: 1rem; }
            .pt-2 { padding-top: 0.5rem; }
            .pt-6 { padding-top: 1.5rem; }
            .pb-6 { padding-bottom: 1.5rem; }
            .p-4 { padding: 1rem; }
            .border-b { border-bottom: 1px solid #e5e7eb; }
            .border-t { border-top: 1px solid #e5e7eb; }
            .border-t-2 { border-top: 2px solid #000; }
            .text-green-600 { color: #059669; }
            .text-red-600 { color: #dc2626; }
            .text-gray-500 { color: #6b7280; }
            .text-gray-600 { color: #4b5563; }
            .text-gray-700 { color: #374151; }
            .text-gray-900 { color: #111827; }
            .bg-gray-50 { background-color: #f9fafb; }
            .rounded-lg { border-radius: 0.5rem; }
            .flex { display: flex; }
            .justify-between { justify-content: space-between; }
            .justify-end { justify-content: flex-end; }
            .items-start { align-items: flex-start; }
            .gap-4 { gap: 1rem; }
            .max-w-xs { max-width: 20rem; }
            .w-80 { width: 20rem; }
            .italic { font-style: italic; }
            .uppercase { text-transform: uppercase; }
            @media print {
              body { padding: 0; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);

    printWindow.document.close();

    // Wait for content to load, then print
    printWindow.onload = function() {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const blob = await paymentService.downloadInvoicePDF(invoiceId);

      // Create download link
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Invoice downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download invoice');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!invoice) {
    return null;
  }

  const isPaid = invoice.status === 'paid';
  const planName = invoice.membership?.plan_name || invoice.plan?.name || 'Membership Plan';
  const duration = invoice.membership?.duration_days || invoice.plan?.duration_days || 30;
  const paymentMethod = invoice.payments && invoice.payments.length > 0
    ? invoice.payments[0].payment_method
    : 'N/A';

  return (
    <div className="max-w-4xl mx-auto">
      {/* Action Buttons - Hide on print */}
      <div className="mb-6 print:hidden">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/members')}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <FiArrowLeft className="mr-2" />
            Back to Members
          </button>

          <div className="flex gap-3">
            {/* Print Invoice button hidden for now */}
            {/* <button
              onClick={handlePrint}
              className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <FiPrinter className="mr-2" />
              Print Invoice
            </button> */}

            <button
              onClick={handleDownload}
              disabled={downloading}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {downloading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Downloading...
                </>
              ) : (
                <>
                  <FiDownload className="mr-2" />
                  Download PDF
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Success Message - Hide on print */}
      {isPaid && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center print:hidden">
          <FiCheck className="text-green-600 text-2xl mr-3" />
          <div>
            <h3 className="text-green-800 font-semibold">Payment Successful!</h3>
            <p className="text-green-700 text-sm">
              Your membership has been activated. Invoice details are shown below.
            </p>
          </div>
        </div>
      )}

      {/* Invoice Container */}
      <div id="invoice-content" className="bg-white shadow-lg rounded-lg p-8 print:shadow-none">
        {/* Header */}
        <div className="border-b pb-6 mb-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {invoice.branch_name || 'RSFitness Gym'}
              </h1>
              {invoice.branch_address && (
                <p className="text-gray-600 mt-2 max-w-xs">{invoice.branch_address}</p>
              )}
              {invoice.branch_phone && (
                <p className="text-gray-600">Phone: {invoice.branch_phone}</p>
              )}
              {invoice.branch_email && (
                <p className="text-gray-600">Email: {invoice.branch_email}</p>
              )}
            </div>

            <div className="text-right">
              <h2 className="text-2xl font-bold text-gray-900">INVOICE</h2>
              <div className="mt-4 text-sm">
                <div className="flex justify-between gap-4 mb-1">
                  <span className="font-semibold text-gray-700">Invoice Number:</span>
                  <span className="text-gray-900">{invoice.invoice_number}</span>
                </div>
                <div className="flex justify-between gap-4 mb-1">
                  <span className="font-semibold text-gray-700">Invoice Date:</span>
                  <span className="text-gray-900">
                    {new Date(invoice.invoice_date).toLocaleDateString('en-IN')}
                  </span>
                </div>
                <div className="flex justify-between gap-4 mb-1">
                  <span className="font-semibold text-gray-700">Due Date:</span>
                  <span className="text-gray-900">
                    {invoice.due_date
                      ? new Date(invoice.due_date).toLocaleDateString('en-IN')
                      : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="font-semibold text-gray-700">Status:</span>
                  <span className={`font-semibold ${
                    isPaid ? 'text-green-600' : 'text-orange-600'
                  }`}>
                    {invoice.status.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bill To */}
        <div className="mb-8">
          <h3 className="text-sm font-bold text-gray-700 mb-3">BILL TO:</h3>
          <div className="text-gray-900">
            <p className="font-semibold text-lg">
              {invoice.first_name} {invoice.last_name}
            </p>
            <p className="text-gray-600">Member ID: {invoice.member_code}</p>
            <p className="text-gray-600">Phone: {invoice.phone}</p>
            <p className="text-gray-600">Email: {invoice.email}</p>
            {invoice.address && (
              <p className="text-gray-600 mt-1">Address: {invoice.address}</p>
            )}
          </div>
        </div>

        {/* Invoice Items Table */}
        <div className="mb-8">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-300">
                <th className="text-left py-3 text-gray-700 font-semibold">Item</th>
                <th className="text-left py-3 text-gray-700 font-semibold">Description</th>
                <th className="text-center py-3 text-gray-700 font-semibold">Qty</th>
                <th className="text-right py-3 text-gray-700 font-semibold">Price</th>
                <th className="text-right py-3 text-gray-700 font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-200">
                <td className="py-4 text-gray-900">Membership</td>
                <td className="py-4 text-gray-600">
                  {planName} ({duration} days)
                </td>
                <td className="py-4 text-center text-gray-900">1</td>
                <td className="py-4 text-right text-gray-900">
                  ₹{parseFloat(invoice.subtotal).toFixed(2)}
                </td>
                <td className="py-4 text-right text-gray-900">
                  ₹{parseFloat(invoice.subtotal).toFixed(2)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="flex justify-end mb-8">
          <div className="w-80">
            <div className="flex justify-between py-2 text-gray-700">
              <span>Subtotal:</span>
              <span>₹{parseFloat(invoice.subtotal).toFixed(2)}</span>
            </div>

            {invoice.discount > 0 && (
              <div className="flex justify-between py-2 text-green-600">
                <span>Discount:</span>
                <span>-₹{parseFloat(invoice.discount).toFixed(2)}</span>
              </div>
            )}

            {invoice.tax_amount > 0 && (
              <div className="flex justify-between py-2 text-gray-700">
                <span>Tax ({invoice.tax_percent}%):</span>
                <span>₹{parseFloat(invoice.tax_amount).toFixed(2)}</span>
              </div>
            )}

            <div className="border-t-2 border-gray-800 mt-2 pt-2">
              <div className="flex justify-between text-xl font-bold text-gray-900">
                <span>Total Amount:</span>
                <span>₹{parseFloat(invoice.total_amount).toFixed(2)}</span>
              </div>
            </div>

            <div className="flex justify-between py-2 text-gray-700 mt-3">
              <span>Amount Paid:</span>
              <span>₹{parseFloat(invoice.amount_paid).toFixed(2)}</span>
            </div>

            {invoice.balance > 0 ? (
              <div className="flex justify-between py-2 text-red-600 font-semibold">
                <span>Balance Due:</span>
                <span>₹{parseFloat(invoice.balance).toFixed(2)}</span>
              </div>
            ) : (
              <div className="flex justify-between py-2 text-green-600 font-semibold">
                <span>Status:</span>
                <span>PAID IN FULL</span>
              </div>
            )}
          </div>
        </div>

        {/* Payment Information */}
        {invoice.payments && invoice.payments.length > 0 && (
          <div className="mb-8 bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-bold text-gray-700 mb-2">PAYMENT INFORMATION</h3>
            <div className="text-gray-600">
              <p>Payment Method: <span className="font-semibold text-gray-900 uppercase">{paymentMethod}</span></p>
              {invoice.payments[0].payment_date && (
                <p>Payment Date: {new Date(invoice.payments[0].payment_date).toLocaleDateString('en-IN')}</p>
              )}
              {invoice.payments[0].transaction_id && (
                <p>Transaction ID: {invoice.payments[0].transaction_id}</p>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="border-t pt-6 text-center">
          {isPaid && (
            <p className="text-gray-600 italic mb-4">Thank you for your business!</p>
          )}
          <p className="text-gray-500 text-sm">
            This is a computer-generated invoice.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Invoice;
